import { Injectable, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  ChildrenOutletContexts,
  NavigationEnd,
  PRIMARY_OUTLET,
  Params,
  Router,
  RouterOutletContract,
} from '@angular/router'

import { filter } from 'rxjs'

import { HarnessSelectionUuidsService } from '@flowx/designer/modules/ai-builder/services/harness-selection-uuids.service'
import { isUuid } from '@flowx/designer/modules/ai-builder/utils/text.util'
import {
  MERGE_CONFLICTS_OUTLET,
  PageType,
  RESOURCE_PAGE_DATA_KEY,
  ResourcePageMarker,
  RunType,
  enumerationValueIds,
  resourcePageId,
} from '@flowx/designer/shared/entities/resource-page.entity'
import type { ComponentCanDeactivate } from '@flowx/designer/shared/guards/pending-changes.guard'

/** The deepest of the route's ids: a page on a version, on an app, in a workspace, or none. */
export type HarnessScope = 'organization' | 'workspace' | 'app' | 'app_version'

/**
 * The `ui_context` a message carries: where the maker is, by ids only. `page.resource_type` is
 * open — the harness accepts a type it does not know yet — while the shape stays strict.
 */
export type HarnessUiContext = {
  schema_version: 2
  scope: HarnessScope
  workspace_id?: string
  app_id?: string
  app_version_id?: string
  page: { type: PageType; resource_type?: string; id?: string }
  /** What the maker picked inside the page; absent when nothing is. */
  selection?: { type: string; id: string }[]
  /** The run a run page shows, by the id its owner service reads it by. */
  run?: { type: RunType; id: string }
  has_unsaved_changes?: boolean
}

/** The workspace, app and version a message is about: the ids its route must not contradict. */
export type HarnessMessageScope = { workspaceId: string; appId: string; appVersionId: string }

/** Read only explicit semantic route metadata. No URLs, names, query contents or form data leave Designer. */
@Injectable({ providedIn: 'root' })
export class HarnessUiContextService {
  readonly #router = inject(Router, { optional: true })
  readonly #outlets = inject(ChildrenOutletContexts, { optional: true })
  readonly #selectionUuids = inject(HarnessSelectionUuidsService)
  readonly pageRevision = signal(0)

  constructor() {
    this.#router?.events
      ?.pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => this.pageRevision.update((revision) => revision + 1))
  }

  /**
   * Whether the maker is holding an edit this application version's harness would not see.
   *
   * The harness reads the last-saved state and commits against it, so a step must not run on top
   * of work that exists only in the browser. Nothing is registered here to make that visible: the
   * answer is read from the `canDeactivate` contract Designer's editors already implement for
   * `PendingChangesGuard`, on the components the primary outlet chain has activated. An editor
   * written later is covered the day it is guarded against losing changes, with nothing to
   * remember on this side.
   *
   * It is a floor, not a guarantee. A component that implements nothing, or answers with an
   * `Observable` there is no way to read synchronously, is unknown rather than clean — `false`
   * means "nothing on the page says otherwise", and the maker's own acknowledgement on the run
   * card is what stands in for the rest.
   */
  hasUnsavedChanges(appVersionId: string): boolean {
    return unsavedChangesOnPage(this.#outlets, appVersionId) === true
  }

  snapshot(scope: HarnessMessageScope): HarnessUiContext | undefined {
    const root = this.#router?.routerState.snapshot.root
    const context = root
      ? contextFromRoute(root, scope, (type, routeId) =>
          this.#selectionUuids.flowxUuidOf(type, routeId)
        )
      : undefined
    if (!context) {
      return undefined
    }
    const unsaved =
      context.page.type === 'resource_editor'
        ? unsavedChangesOnPage(this.#outlets, scope.appVersionId)
        : undefined

    return unsaved === undefined ? context : { ...context, has_unsaved_changes: unsaved }
  }
}

/**
 * The dirty state of the page the maker is on, or `undefined` where nothing there can answer —
 * which is never reported as clean.
 *
 * Only the primary chain is walked, the same one the route metadata is read from: a component in a
 * named outlet is beside the editor, not the editor.
 */
export function unsavedChangesOnPage(
  outlets: ChildrenOutletContexts | null,
  appVersionId: string
): boolean | undefined {
  let context = outlets?.getContext(PRIMARY_OUTLET) ?? null
  let unsaved: boolean | undefined
  while (context) {
    const answer = ownsVersion(context.route, appVersionId) ? unsavedIn(context.outlet) : undefined

    if (answer !== undefined) {
      unsaved = (unsaved ?? false) || answer
    }
    context = context.children.getContext(PRIMARY_OUTLET)
  }
  return unsaved
}

function ownsVersion(route: ActivatedRoute | null, appVersionId: string): boolean {
  return (
    !!appVersionId &&
    !!route?.snapshot.pathFromRoot.some((entry) => entry.params['appVersionId'] === appVersionId)
  )
}

function unsavedIn(outlet: RouterOutletContract | null): boolean | undefined {
  const editor = (
    outlet?.isActivated ? outlet.component : null
  ) as Partial<ComponentCanDeactivate> | null

  if (typeof editor?.canDeactivate !== 'function') {
    return undefined
  }
  try {
    const canLeave = editor.canDeactivate()

    // An `Observable` answer is a question asked over time; there is no synchronous reading of it.
    return typeof canLeave === 'boolean' ? !canLeave : undefined
  } catch {
    // An editor that cannot say where it stands has not said it is clean.
    return undefined
  }
}

/**
 * What the route says, merged root to leaf over the primary chain and then the merge-conflicts
 * modal open over it: the deepest marker wins, so the modal is the page while it is open.
 */
type RouteReading = {
  marker: ResourcePageMarker | null | undefined
  params: Params
  queryParams: Params
}

const primaryChain = (route: ActivatedRouteSnapshot): ActivatedRouteSnapshot[] => {
  const child = route.children.find((candidate) => candidate.outlet === PRIMARY_OUTLET)

  return [route, ...(child ? primaryChain(child) : [])]
}

const mergeConflictsChain = (primary: ActivatedRouteSnapshot[]): ActivatedRouteSnapshot[] => {
  const modal = primary
    .flatMap((route) => route.children)
    .find((child) => child.outlet === MERGE_CONFLICTS_OUTLET)

  return modal ? primaryChain(modal) : []
}

const readRoute = (root: ActivatedRouteSnapshot): RouteReading => {
  const primary = primaryChain(root)

  return [...primary, ...mergeConflictsChain(primary)].reduce<RouteReading>(
    (reading, route) => ({
      marker: Object.prototype.hasOwnProperty.call(route.data, RESOURCE_PAGE_DATA_KEY)
        ? (route.data[RESOURCE_PAGE_DATA_KEY] as ResourcePageMarker | null)
        : reading.marker,
      params: { ...reading.params, ...route.params },
      queryParams: { ...reading.queryParams, ...route.queryParams },
    }),
    { marker: undefined, params: {}, queryParams: {} }
  )
}

const uuidOf = (value: unknown): string | undefined =>
  typeof value === 'string' && isUuid(value) ? value : undefined

/** Deepest first: the scope is the narrowest id the route carries. */
const SCOPE_BY_PARAM: readonly (readonly [string, HarnessScope])[] = [
  ['appVersionId', 'app_version'],
  ['appId', 'app'],
  ['workspaceId', 'workspace'],
]

/** Ids that are not UUIDs (a workflow instance's Mongo id, a node's flowxUuid), never free text. */
const SAFE_ID = /^[A-Za-z0-9_-]{1,128}$/

/** The harness reads at most this many picked items. */
const MAX_SELECTION = 20

/** An enum value opened on the enumeration values page, from its breadcrumb. */
const ENUM_VALUE_SELECTION = 'enum-value'

const idOf = (value: unknown): string | undefined =>
  typeof value === 'string' && SAFE_ID.test(value) ? value : undefined

/** A param from the path, else from the query: pages load a run either way. */
const paramOf = (reading: RouteReading, name: string): unknown =>
  reading.params[name] ?? reading.queryParams[name]

function runOf(reading: RouteReading): HarnessUiContext['run'] {
  const run = reading.marker?.run
  const id = run && idOf(paramOf(reading, run.param))

  return run && id ? { type: run.type, id } : undefined
}

/**
 * The picks a route names by a database id, which the harness must never be sent: each goes by its
 * flowxUuid, which only the open editor can tell, or not at all.
 */
const PICKED_BY_FLOWX_UUID: ReadonlySet<string> = new Set([
  'process-node',
  'node-action',
  'boundary-event',
  'navigation-area',
  'ui-component',
  'ui-flow-page',
])

/** The flowxUuid of a pick of `type` the route names by `routeId`, when an open editor knows it. */
export type SelectionFlowxUuidOf = (type: string, routeId: string) => string | undefined

const noOpenEditor: SelectionFlowxUuidOf = () => undefined

/**
 * Each child param present, in marker order; a comma list (`?templateIds=a,b`) is several picks.
 * A pick named twice is sent once. On the enumeration values page, the values opened under the
 * root are picks too, by id, never by name.
 */
function selectionOf(
  reading: RouteReading,
  flowxUuidOf: SelectionFlowxUuidOf
): NonNullable<HarnessUiContext['selection']> {
  const picks = Object.entries(reading.marker?.children ?? {}).flatMap(([param, type]) => {
    const value = paramOf(reading, param)
    const routeIds = typeof value === 'string' ? value.split(',') : []

    return routeIds.map(idOf).flatMap((routeId) => {
      const id =
        routeId && (PICKED_BY_FLOWX_UUID.has(type) ? idOf(flowxUuidOf(type, routeId)) : routeId)

      return id ? [{ type, id }] : []
    })
  })
  const enumValues = reading.marker?.enumerationPath
    ? enumerationValueIds(reading.queryParams).flatMap((value) => {
        const id = idOf(value)

        return id ? [{ type: ENUM_VALUE_SELECTION, id }] : []
      })
    : []

  return [
    ...new Map([...picks, ...enumValues].map((pick) => [`${pick.type}:${pick.id}`, pick])).values(),
  ].slice(0, MAX_SELECTION)
}

/** A page that cannot be described is `other`; a marked editor without a valid id is one. */
function pageOf(reading: RouteReading): HarnessUiContext['page'] {
  const { marker, params, queryParams } = reading
  if (!marker) {
    return { type: 'other' }
  }
  if (marker.pageType) {
    return { type: marker.pageType, resource_type: marker.type }
  }
  const hasResource = !!(marker.idParam || marker.enumerationPath)
  if (marker.run && !hasResource) {
    return { type: runOf(reading) ? 'run_detail' : 'run_list', resource_type: marker.type }
  }
  const id = uuidOf(resourcePageId(marker, params, queryParams))
  if (hasResource && !id) {
    return { type: 'other' }
  }

  return id
    ? { type: 'resource_editor', resource_type: marker.type, id }
    : { type: 'resource_list', resource_type: marker.type }
}

/** The route params that, where the route carries them, must name the message's own scope. */
const MESSAGE_SCOPE_PARAMS = ['workspaceId', 'appId', 'appVersionId'] as const

/**
 * Where the maker is, for a message about `scope`. Ids come from route params, and picks named by a
 * database id from `flowxUuidOf`. A route in another workspace, app or version than the message's
 * says nothing: its page is not the one the message is about. Unknown editor dirty state is left
 * out, never claimed clean.
 */
export function contextFromRoute(
  root: ActivatedRouteSnapshot,
  scope: HarnessMessageScope,
  flowxUuidOf: SelectionFlowxUuidOf = noOpenEditor
): HarnessUiContext | undefined {
  const reading = readRoute(root)
  const { params } = reading
  if (
    MESSAGE_SCOPE_PARAMS.some(
      (param) => params[param] !== undefined && params[param] !== scope[param]
    )
  ) {
    return undefined
  }
  const workspaceId = uuidOf(params['workspaceId'])
  const appId = uuidOf(params['appId'])
  const routeVersionId = uuidOf(params['appVersionId'])
  const page = pageOf(reading)
  const selection = selectionOf(reading, flowxUuidOf)
  const run = runOf(reading)

  return {
    schema_version: 2,
    scope: SCOPE_BY_PARAM.find(([param]) => uuidOf(params[param]))?.[1] ?? 'organization',
    ...(workspaceId && { workspace_id: workspaceId }),
    ...(appId && { app_id: appId }),
    ...(routeVersionId && { app_version_id: routeVersionId }),
    page,
    ...(selection.length > 0 && { selection }),
    ...(run && { run }),
  }
}
